/// <amd-module name="Build/Admin/Pool/Components/SecurityContainer" />
import * as React from "react";

import { VssComponent, IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageService, IPageData } from "VSS/Platform/Page";

import { IProjectData } from "Tfs/Platform/Page";

import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";

import { TaskAgentQueue, TaskAgentPool } from "DistributedTask/Client/Contracts/TaskAgent";

import { PoolRoles, PoolRolesScope } from "Build/Admin/Common/Components/PoolRoles";
import { StoreStatus, FwLinks } from "Build/Admin/Common/Constants";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolActionCreator } from "Build/Admin/Pool/Actions/PoolActionCreator";
import { PoolPipelineSecurity } from "Build/Admin/Pool/Components/PoolPipelineSecurity";
import { PipelinesAuthorizationComponent } from "DistributedTaskUI/SecurityComponent/PipelinePermissionsAuthorization/Components/PipelinesAuthorizationComponent"
import { StoresHub } from "Build/Admin/Pool/Stores/StoresHub";

import * as Resources from "Build/Admin/Pool/Resources";

const rolesScopeProjectId: string = "roles-scope-project";
const rolesScopeCollectionId: string = "roles-scope-collection";
const helpURL: string = "https://aka.ms/SCAuthorization" //TO UPDATE

export interface ISecurityContainerProps {
    actionCreator: PoolActionCreator;
    storesHub: StoresHub;
}

export interface ISecurityContainerState {
    selectedAgentQueue: TaskAgentQueue | undefined;
    selectedAgentPool: TaskAgentPool | undefined;
    hasManageQueuePermission: boolean;
    hasManagePoolPermission: boolean;
    hasManageAllQueuesPermission: boolean;
    hasManageAllPoolsPermission: boolean;
    pipelinesAuthorizedForQueue: boolean;
    status: StoreStatus;
    selectedRolesScope: string;
}

export class SecurityContainer extends VssComponent<ISecurityContainerProps, ISecurityContainerState> {
    private _onPrem: boolean;
    private _project: IProjectData | undefined;

    constructor(props: ISecurityContainerProps, context: IVssComponentContext) {
        super(props, context);

        const vssPageService: IVssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const projectDataService: IProjectDataService = context.pageContext.getService<IProjectDataService>("IProjectDataService");
        const vssPageData: IPageData | undefined = vssPageService.getData();

        if (vssPageData) {
            this._onPrem = !vssPageData.isHosted;
        }

        this._project = projectDataService.getProjectData();

        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.storesHub.selectionStore.addChangedListener(this._onChanged);
        this.props.storesHub.securityStore.addChangedListener(this._onChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.selectionStore.removeChangedListener(this._onChanged);
        this.props.storesHub.securityStore.removeChangedListener(this._onChanged);
    }

    public render(): JSX.Element {
        return (
            <div className="pipelines-pool-security">
                <ConditionalChildren renderChildren={this.state.selectedAgentQueue !== undefined}>
                    <div className="page-content-top page-content-left page-content-right">
                        { isFeatureFlagEnabled(this.context.pageContext, "Pipelines.Policy.AllowAuthorizingOnlySomePipelineForQueues", false) ?
                            <PipelinesAuthorizationComponent
                                resourceId={ this.state.selectedAgentQueue ? this.state.selectedAgentQueue.id.toString() : '' }
                                resourceType="queue"
                                noAuthorizedPipelinesViewLearnMoreLink={FwLinks.AgentPoolsSecurityUrl}
                                noRestrictionsViewLearnMoreLink={FwLinks.AgentPoolsSecurityUrl} 
                                hideRestrictPermissionButton={this.state.selectedAgentPool && this.state.selectedAgentPool.isHosted}
                                canEditPipelinePermission={this._hasPipelineSecurityManagePermission()}
                                canOpenAccess={this._hasPipelineSecurityManageAllPermission()}
                                canRestrictAccess={this._hasPipelineSecurityManagePermission()} />
                            :
                            <Card className="flex-grow" titleProps={{ text: Resources.PipelineSecurityTitle, size: TitleSize.Medium }}>
                                <PoolPipelineSecurity
                                    allPipelinesAuthorized={this._getPipelinesAuthorizedForQueue()}
                                    managePermission={this._hasPipelineSecurityManagePermission()}
                                    status={this.state.status}
                                    onSave={this._onPipelineSecuritySave} />
                            </Card>
                        }
                    </div>
                </ConditionalChildren>
                <div className="page-content-top page-content">
                    <Card className="roles-card flex-grow"
                        titleProps={{text: Resources.UserSecurityTitle, size: TitleSize.Medium}}
                        headerCommandBarItems={this._getRolesCommandBarItems()}>
                        <PoolRoles
                            selectedRolesScope={this.state.selectedRolesScope === rolesScopeProjectId ? PoolRolesScope.Queue : PoolRolesScope.Pool}
                            selectedAgentQueueOrPool={this.state.selectedRolesScope === rolesScopeProjectId ? this.state.selectedAgentQueue : this.state.selectedAgentPool}
                            hasManagePermission={this.state.selectedRolesScope === rolesScopeProjectId ? this.state.hasManageQueuePermission : this.state.hasManagePoolPermission} />
                    </Card>
                </div>
            </div>
        );
    }

    private _getRolesCommandBarItems(): IHeaderCommandBarItem[] {
        if (this._project) {
            const collectionScopeText: string = this._onPrem ? Resources.RolesScopeCollection : Resources.RolesScopeOrganization;
            const selectedScopeText: string = this.state.selectedRolesScope === rolesScopeProjectId ? Resources.RolesScopeProject : collectionScopeText;
            return [
                {
                    id: "roles-card-header",
                    text: selectedScopeText,
                    subMenuProps: {
                        id: "roles-scope-menu",
                        items: [
                            {
                                id: rolesScopeProjectId,
                                text: Resources.RolesScopeProject,
                                onActivate: this._onRolesScopeMenuItemActivate
                            },
                            {
                                id: rolesScopeCollectionId,
                                text: collectionScopeText,
                                onActivate: this._onRolesScopeMenuItemActivate
                            }
                        ]
                    }
                }
            ];
        }
        else {
            return [];
        }
    }

    private _onRolesScopeMenuItemActivate = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>): void => {
        this.setState({
            selectedRolesScope: menuItem.id
        });
    }

    private _getPipelinesAuthorizedForQueue(): boolean {
        // Pipelines are always authorized to run on hosted pools, so simply return 'true' in that case
        if (this.state.selectedAgentPool && this.state.selectedAgentPool.isHosted) {
            return true;
        }

        return this.state.pipelinesAuthorizedForQueue;
    }

    private _hasPipelineSecurityManagePermission(): boolean {
        // Pipelines are always authorized to run on hosted pools, so don't let the user change this setting in that case
        if (this.state.selectedAgentPool && this.state.selectedAgentPool.isHosted) {
            return false;
        }

        return this.state.selectedAgentQueue ? this.state.hasManageQueuePermission : this.state.hasManagePoolPermission;
    }

    private _hasPipelineSecurityManageAllPermission(): boolean {
        if (this.state.selectedAgentPool && this.state.selectedAgentPool.isHosted) {
            return false;
        }

        return this.state.selectedAgentQueue ? this.state.hasManageAllQueuesPermission : this.state.hasManageAllPoolsPermission;    
    }

    private _onPipelineSecuritySave = (allPipelinesAuthorized: boolean): void => {
        if (this.state.selectedAgentQueue &&
            this._hasPipelineSecurityManagePermission()) {
            this.props.actionCreator.setPipelinesAuthorizedForQueue(this.state.selectedAgentQueue, allPipelinesAuthorized);
        }
    }

    private _onChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores(): ISecurityContainerState {
        return {
            selectedAgentQueue: this.props.storesHub.selectionStore.state.selectedAgentQueue,
            selectedAgentPool: this.props.storesHub.selectionStore.state.selectedAgentPool,
            hasManageQueuePermission: this.props.storesHub.securityStore.state.hasManageQueuePermission,
            hasManagePoolPermission: this.props.storesHub.securityStore.state.hasManagePoolPermission,
            hasManageAllQueuesPermission: this.props.storesHub.securityStore.state.hasManageAllQueuesPermission,
            hasManageAllPoolsPermission: this.props.storesHub.securityStore.state.hasManageAllPoolsPermission,
            pipelinesAuthorizedForQueue: this.props.storesHub.securityStore.state.pipelinesAuthorizedForQueue,
            status: this.props.storesHub.securityStore.state.status,
            selectedRolesScope: this._project ? rolesScopeProjectId : rolesScopeCollectionId
        };
    }
}
